/* eslint-disable complexity */
import React, { Component } from 'react';

import GraphBarIcon from '@atlaskit/icon/glyph/graph-bar';
import TableIcon from '@atlaskit/icon/glyph/table';

import {
  ProjectIsntAvailable,
  ReportFilters,
  ReportHeader,
  ReportTable,
} from '@qality/common-components';
import Loading from '@qality/common-components/src/atoms/Loading/Loading';
import { PieChartIcon } from '@qality/common-components/src/atoms/icons/PieChartIcon';
import { Dropdown } from '@qality/common-components/src/molecules/Dropdown';
import { ReportChart } from '@qality/common-components/src/report/ReportChart';
import { FEATURE_FLAG, IS_CLOUD } from '@qality/common-config';
import { Project, Report } from '@qality/common-model';
import {
  ProjectService,
  ReportService,
  SettingsService,
  StorageOfUniqueServices,
} from '@qality/common-service';
import { ProjectsAvailabilityContext } from '@qality/common-state/src/ProjectsAvailabilityContext';
import { Utils, XLSXExporter } from '@qality/common-utils';

import ExportToExcelButton from './ExportToExcelButton';
import {
  ReportPageFiltersWrapperStyled,
  ReportPageHeaderActionsStyled,
} from './ReportPage.styled';
import './_report.scss';

type Props = Record<string, never>;

type FilterOption = {
  name: string;
  value: string | number;
};

type PresentationType = {
  label: string;
  value: 'statusChart' | 'reportTable' | 'assigneeChart';
  icon: JSX.Element;
  isDisabled: boolean;
  tooltip?: string;
};

type State = {
  canView: boolean;
  loading: boolean;
  report: Report;
  project: Project;
  presentationType: PresentationType;
  filterOptions: FilterOption[];
  reportTableKey: number;
  isReportDataLoading: boolean;
};

const dropdownOptions: PresentationType[] = [
  {
    label: 'Executions Table',
    value: 'reportTable',
    icon: <TableIcon size='small' label='Executions table' />,
    isDisabled: false,
  },
  {
    label: 'Status Chart',
    value: 'statusChart',
    icon: <PieChartIcon />,
    isDisabled: !FEATURE_FLAG.isPremium,
    tooltip: !FEATURE_FLAG.isPremium
      ? 'Upgrade to QAlity Plus to unlock Status Chart.'
      : null,
  },
  {
    label: 'Assignee Chart',
    value: 'assigneeChart',
    icon: <GraphBarIcon size='small' label='Clone' />,
    isDisabled: !FEATURE_FLAG.isPremium,
    tooltip: !FEATURE_FLAG.isPremium
      ? 'Upgrade to QAlity Plus to unlock Assignee Chart.'
      : null,
  },
];

export default class ReportPage extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      canView: false,
      loading: true,
      report: null,
      project: null,
      presentationType: dropdownOptions[0],
      filterOptions: null,
      reportTableKey: new Date().getTime(),
      isReportDataLoading: true,
    };
  }
  static contextType = ProjectsAvailabilityContext;

  exportExcel = async (): Promise<void> => {
    const filters = {};
    this.state.filterOptions.forEach((filter) => {
      filters[filter.name] = filter.value;
    });
    await XLSXExporter.export(this.state.report, filters);
  };

  async componentDidMount(): Promise<void> {
    let urlProjectId = (
      await StorageOfUniqueServices.get('URLService').getURLSearchParams()
    ).get('projectId');
    const contextProjectId = await StorageOfUniqueServices.get(
      'ContextParamsService'
    ).getProjectId();

    urlProjectId = urlProjectId || contextProjectId;

    const reportName = await StorageOfUniqueServices.get(
      'ContextParamsService'
    ).getReportName();
    await this.fetchReport(urlProjectId, contextProjectId, reportName);
  }

  fetchReport = async (
    urlProjectId: string,
    contextProjectId: string,
    reportName: string
  ): Promise<void> => {
    if (!Utils.isNilOrEmpty(contextProjectId)) {
      let report;
      try {
        const project = await ProjectService.getProject(contextProjectId);
        report = ReportService.getReportByName(reportName);
        await this.updateUrlParams(report, urlProjectId);

        this.setState({
          loading: false,
          canView: true,
          report,
          project,
        });
      } catch (e) {
        if (e.message === 'NO_PROJECTS') {
          this.setState({
            loading: false,
            canView: true,
            report,
          });
        } else {
          this.setState({
            loading: false,
            canView: false,
          });
        }
      }
    } else {
      this.setState({
        loading: false,
        canView: false,
      });
    }
  };

  updateUrlParams = async (
    report: Report,
    projectId: string
  ): Promise<void> => {
    if (projectId === 'ALL_PROJECTS') {
      await StorageOfUniqueServices.get('URLService').updateUrlParams([
        { name: 'projectId', value: 'ALL_PROJECTS' },
      ]);
      return;
    }
    const project = await ProjectService.getProject(projectId);
    const isPluginEnabled = await this.isPluginEnabledInProject(project);
    if (
      isPluginEnabled === true ||
      isPluginEnabled === undefined ||
      isPluginEnabled === null
    ) {
      await StorageOfUniqueServices.get('URLService').updateUrlParams([
        { name: 'projectId', value: projectId },
      ]);
    } else if (this.isProjectInFilterRequired(report)) {
      const projectWithEnabledQAlity = await this.getFirstAvailableProject();
      await StorageOfUniqueServices.get('URLService').updateUrlParams([
        { name: 'projectId', value: projectWithEnabledQAlity.id },
      ]);
    } else {
      await StorageOfUniqueServices.get('URLService').updateUrlParams([
        { name: 'projectId', value: '' },
      ]);
    }
  };

  async isPluginEnabledInProject(project: Project): Promise<boolean> {
    if (this.context.projectsAvailabilityByProjectKey === undefined) {
      return (await SettingsService.getSettingsForProject(project))
        .isPluginEnabled;
    } else {
      const projectAvailability =
        this.context.projectsAvailabilityByProjectKey[project.key];
      return projectAvailability?.isPluginEnabled;
    }
  }

  getFirstAvailableProject = async (): Promise<Project> => {
    const params = {
      limit: 1,
      filterByQAlitySettings: true,
      projectsAvailabilityByProjectKey:
        this.context.projectsAvailabilityByProjectKey,
    };
    const projectsWithEnabledQAlity = await StorageOfUniqueServices.get(
      'ProjectServiceUnique'
    ).getProjectsPaginated(params);
    if (projectsWithEnabledQAlity.length === 0) {
      throw new Error('NO_PROJECTS');
    }
    return projectsWithEnabledQAlity[0];
  };

  isProjectInFilterRequired(report: Report): boolean {
    return (
      report.filters.projectSelect &&
      (!FEATURE_FLAG.isPremium ||
        (!report.filters.projectSelect.isClearable &&
          !report.filters.projectSelect.useAllOption))
    );
  }

  setFilterOptions = (filterOptions: FilterOption[]): void => {
    this.setState({ filterOptions });
  };

  refreshReport = async (filterOptions: FilterOption[]): Promise<void> => {
    await StorageOfUniqueServices.get('URLService').setUrlParams(filterOptions);
    this.setState({ filterOptions, reportTableKey: new Date().getTime() });
  };

  handleChangePresentationType = (value: PresentationType): void => {
    this.setState({ presentationType: value });
  };

  handleFetchReportDataLoading = (value: boolean): void => {
    this.setState({ isReportDataLoading: value });
  };

  render(): React.ReactNode {
    if (this.state.loading) {
      return <Loading relative={!IS_CLOUD} />;
    } else if (!this.state.canView) {
      return <ProjectIsntAvailable />;
    } else {
      return (
        <div id='report-page-container'>
          <ReportHeader
            project={this.state.project}
            name={this.state.report.name}
            description={this.state.report.description}
            actions={
              <ReportPageHeaderActionsStyled>
                {this.state.report.name === 'Test Execution Report' &&
                  this.state.presentationType.value === 'reportTable' && (
                    <ExportToExcelButton exportExcel={this.exportExcel} />
                  )}
                {this.state.report.showChartDropdown && (
                  <Dropdown
                    position='bottom-end'
                    options={dropdownOptions}
                    currentValue={this.state.presentationType}
                    onChange={this.handleChangePresentationType}
                  />
                )}
              </ReportPageHeaderActionsStyled>
            }
          />
          <ReportPageFiltersWrapperStyled>
            <ReportFilters
              mapFilters={this.state.report.mapFilters}
              onGenerateReport={this.refreshReport}
              isDisabled={plugin.isFree}
              onLoad={this.setFilterOptions}
              projectSelect={this.state.report.filters.projectSelect}
              requirementSelect={this.state.report.filters.requirementSelect}
              searchSelect={this.state.report.filters.searchSelect}
              userSelect={this.state.report.filters.userSelect}
              dateRangeSelect={this.state.report.filters.dateRangeSelect}
              statusSelect={this.state.report.filters.statusSelect}
              moreFiltersSelect={this.state.report.filters.moreFiltersSelect}
              testCycleSelect={this.state.report.filters.testCycleSelect}
              testTypeSelect={this.state.report.filters.testTypeSelect}
              getDefaultDates={this.state.report.getDefaultDates}
              isLoading={this.state.isReportDataLoading}
            />
            {this.state.report.modals &&
              this.state.report.modals.map((Modal, index) => (
                <Modal key={index} />
              ))}
          </ReportPageFiltersWrapperStyled>

          {this.state.presentationType.value === 'reportTable' ? (
            <ReportTable
              report={this.state.report}
              key={this.state.reportTableKey}
              noData={this.state.project === null}
              isLoading={this.state.isReportDataLoading}
              setIsLoading={this.handleFetchReportDataLoading}
            />
          ) : (
            <ReportChart
              chartName={this.state.presentationType.value}
              filterOptions={this.state.filterOptions}
              isLoading={this.state.isReportDataLoading}
              setIsLoading={this.handleFetchReportDataLoading}
            />
          )}
        </div>
      );
    }
  }
}
